import React, { useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    useTheme,
    CircularProgress
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { Moment } from 'moment';
import PortalCard from '../../../../../components/PortalCard';
import analyticsService from '../../../../../services/analytics/AnalyticsService';

interface UniqueViewsSparklineProps {
    title: string;
    subtitle?: string;
    dateRange?: [Moment, Moment];
}

const UniqueViewsSparkline: React.FC<UniqueViewsSparklineProps> = ({ title, subtitle, dateRange }) => {
    const theme = useTheme();
    const [chartData, setChartData] = React.useState<Array<{ timePeriod: string; uniqueViews: number }>>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const startDate = dateRange?.[0]?.toISOString() || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
                const endDate = dateRange?.[1]?.toISOString() || new Date().toISOString();
                
                const result = await analyticsService.getProductViewsOverTime(
                    startDate,
                    endDate,
                    'day'
                );

                if (result.success && result.data) {
                    setChartData(result.data);
                } else {
                    setError(result.message || 'Failed to fetch data');
                }
            } catch (err: any) {
                setError(err.message || 'Failed to fetch data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateRange]);

    const chartColor = theme.palette.success.main;
    const values = useMemo(() => chartData.map(item => item.uniqueViews), [chartData]);

    return (
        <PortalCard
            sx={{
                p: 2.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant='body2' color='text.secondary'>
                        {subtitle}
                    </Typography>
                )}
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 180, width: '100%' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='error'>
                            {error}
                        </Typography>
                    </Box>
                ) : chartData.length > 0 ? (
                    <LineChart
                        xAxis={[{
                            data: Array.from({ length: chartData.length }, (_, i) => i),
                            scaleType: 'point',
                            label: '',
                            tickLabelStyle: { fontSize: '0.7rem', fill: theme.palette.text.secondary }
                        }]}
                        series={[{
                            data: values,
                            label: 'Unique Views',
                            color: chartColor,
                            curve: 'natural',
                            area: true,
                            showMark: ({ index }) => index % 5 === 0 || index === values.length - 1,
                        }]}
                        height={180}
                        margin={{ top: 10, right: 10, bottom: 30, left: 30 }}
                        slotProps={{
                            legend: { hidden: true }
                        }}
                        sx={{
                            '.MuiLineElement-root': {
                                strokeWidth: 2,
                            },
                            '.MuiMarkElement-root': {
                                r: 3,
                                fill: chartColor,
                            },
                            '.MuiAreaElement-root': {
                                fillOpacity: 0.1,
                            },
                            '& .MuiChartsAxis-tickLabel': {
                                fontSize: '0.7rem',
                                fill: theme.palette.text.secondary,
                            },
                            '& .MuiChartsAxis-line': {
                                stroke: theme.palette.divider,
                                strokeWidth: 1,
                            },
                            '& .MuiChartsAxis-tick': {
                                stroke: theme.palette.divider,
                            },
                        }}
                    />
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='text.secondary'>
                            No data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default UniqueViewsSparkline;

